/**
 * Wholesale Overview — the landing cockpit. Reuses GET /api/leads/state-counts
 * ({ all, by_state }), GET /api/leads/appointments, and GET /api/leads?state=consented.
 */
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import DialMomentum from './DialMomentum';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const PIPELINE = ['new', 'consented', 'contacted', 'responded_warm', 'appointment_set', 'offered', 'under_contract'];
const STATE_LABEL = { new:'New', consented:'Consented', contacted:'Contacted', responded_warm:'Warm', appointment_set:'Appt set', offered:'Offered', under_contract:'Contract' };

function fmtET(iso) {
  return new Intl.DateTimeFormat('en-US', { timeZone:'America/New_York', weekday:'short', month:'short', day:'numeric', hour:'numeric', minute:'2-digit' }).format(new Date(iso)) + ' ET';
}

export default function WholesaleOverview({ onNavigate }) {
  const { authFetch } = useAuth();
  const [counts, setCounts] = useState(null);   // { all, by_state }
  const [appts, setAppts] = useState([]);
  const [inbound, setInbound] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [c, a, i] = await Promise.all([
          authFetch(`${API_BASE}/api/leads/state-counts`).then(r => r.json()),
          authFetch(`${API_BASE}/api/leads/appointments`).then(r => r.json()),
          authFetch(`${API_BASE}/api/leads?state=consented`).then(r => r.json()),
        ]);
        if (!alive) return;
        setCounts(c.data || { all: 0, by_state: {} });
        setAppts(a.data || []);
        setInbound(i.data || []);
      } catch { /* friendly empties below */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [authFetch]);

  if (loading) return <div className="ws-pulse">Loading…</div>;

  const byState = counts?.by_state || {};
  const activeLeads = counts?.all ?? 0;

  return (
    <div className="ws-ov">
      <DialMomentum variant="band" />

      {/* KPI stat tiles — .sol-card supplies the Atelier glass + gold
          catch-light; .ws-kpi layers the stat-tile label/value roles. */}
      <div className="ws-kpis">
        <div className="ws-kpi sol-card"><span>Active leads</span><b>{activeLeads}</b></div>
        <div className="ws-kpi sol-card"><span>Upcoming appts</span><b>{appts.length}</b></div>
        <div className="ws-kpi sol-card"><span>Consented</span><b>{byState.consented || 0}</b></div>
      </div>

      <div className="ws-label">Pipeline</div>
      <div className="ws-pipe">
        {PIPELINE.map((s) => (
          <div key={s} className="ws-stage"><b>{byState[s] || 0}</b><span>{STATE_LABEL[s]}</span></div>
        ))}
      </div>

      <button className="ws-card ws-link sol-card" onClick={() => onNavigate('appointments')}>
        <h4>Upcoming appointments →</h4>
        {appts.length === 0
          ? <p className="ws-empty">No upcoming appointments.</p>
          : appts.slice(0, 4).map((a) => (
              <div key={a.id} className="ws-row">
                <span>{a.property_address || a.owner_full_name || 'Lead'}</span>
                <span>{fmtET(a.scheduled_for)}</span>
              </div>
            ))}
      </button>

      <button className="ws-card ws-link sol-card" onClick={() => onNavigate('inbound')}>
        <h4>Recent inbound →</h4>
        {inbound.length === 0
          ? <p className="ws-empty">No inbound yet.</p>
          : inbound.slice(0, 4).map((l) => (
              <div key={l.id} className="ws-row">
                <span>{l.property_address || 'Lead'}</span>
                <span className="ws-src">{l.source_list_name ? `src: ${l.source_list_name}` : 'funnel'}</span>
              </div>
            ))}
      </button>
    </div>
  );
}
